import { useState, useEffect } from "react";
import Papa from "papaparse";
import { DataItem, HeaderData } from "../types";

interface UseDataLoaderResult {
  data: DataItem[];
  headerData: HeaderData;
  loading: boolean;
  error: string | null;
}

const FILEPATH = "/data/data.csv";

export const useDataLoader = (): UseDataLoaderResult => {
  const [data, setData] = useState<DataItem[]>([]);
  const [headerData, setHeaderData] = useState<HeaderData>({} as HeaderData);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const parseCSVData = (
    results: Papa.ParseResult<string[]>,
  ): { data: DataItem[]; headerData: HeaderData } => {
    const parsedData: DataItem[] = [];
    const headerData: HeaderData = {
      name: results.data[0][0],
      northernDate: results.data[0][2],
      southernDate: results.data[0][1],
      time: results.data[0][4],
      price: results.data[0][5],
      type: results.data[0][6],
      size: results.data[0][17],
      image: results.data[0][18],
      location: results.data[0][3],
    };

    for (let index = 1; index < results.data.length; index++) {
      const row = results.data[index];
      if (row.length < 19) continue; // Skip invalid rows

      parsedData.push({
        name: row[0],
        northernDate: row[2],
        southernDate: row[1],
        time: row[4],
        price: parseInt(row[5]) || 0,
        type: row[6],
        size: row[17],
        image: row[18],
        northStartMonth1: parseInt(row[13]) || -1,
        northStartMonth2: parseInt(row[15]) || -1,
        northEndMonth1: parseInt(row[14]) || -1,
        northEndMonth2: parseInt(row[16]) || -1,
        southStartMonth1: parseInt(row[9]) || -1,
        southStartMonth2: parseInt(row[11]) || -1,
        southEndMonth1: parseInt(row[10]) || -1,
        southEndMonth2: parseInt(row[12]) || -1,
        location: row[3],
      });
    }

    return { data: parsedData, headerData };
  };

  const loadData = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(FILEPATH);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch data: ${response.status} ${response.statusText}`,
        );
      }

      const text = await response.text();

      Papa.parse<string[]>(text, {
        complete: (results: Papa.ParseResult<string[]>) => {
          try {
            const parsedData = parseCSVData(results);
            setData(parsedData.data);
            setHeaderData(parsedData.headerData);
          } catch (parseError) {
            setError(
              `Error parsing CSV data: ${
                parseError instanceof Error
                  ? parseError.message
                  : "Unknown error"
              }`,
            );
          } finally {
            setLoading(false);
          }
        },
        error: (error: Error) => {
          setError(`CSV parsing error: ${error.message}`);
          setLoading(false);
        },
      });
    } catch (fetchError) {
      setError(
        `Network error: ${
          fetchError instanceof Error ? fetchError.message : "Unknown error"
        }`,
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return { data, headerData, loading, error };
};
